// Pure countdown math for the discussion timer (audit P2).
//
// The timer payload's startedAt/expiresAt are SERVER wall-clock ms. Rendering
// them against the raw device clock made any skew shift the countdown 1:1 (a
// phone two minutes behind showed a 5-minute timer as seven). The server now
// stamps `serverNow` onto every projected timer; the client samples
// `serverNow - Date.now()` once per broadcast and renders the countdown in
// server time. Extracted from DiscussionTimer.tsx so the math is testable
// without a DOM.

import type { DiscussionTimer } from '../../shared/src/types.js';

// Seconds remaining at/under which the timer escalates into the urgent tier
// (red clock + pulsing bar) before it fully expires.
export const URGENT_SEC = 10;

/** Server-minus-client clock offset, sampled when a broadcast lands. Add it
 *  to the local clock to get the server clock. ~0 on a well-synced device;
 *  the error bound is one-way network latency (tens of ms on a LAN) versus
 *  unbounded device-clock skew. Falls back to 0 (raw client clock) when the
 *  payload carries no stamp. */
export function serverClockOffset(
  serverNow: number | undefined,
  clientNow: number,
): number {
  return serverNow === undefined ? 0 : serverNow - clientNow;
}

export interface TimerDisplay {
  remainingMs: number;
  min: number;
  sec: number;
  expired: boolean;
  urgent: boolean;
  /** 0..1 fraction of the full duration remaining (drives the progress bar). */
  pct: number;
}

/** Countdown state at local time `clientNow`, rendered in server time via
 *  `offsetMs` (see serverClockOffset). Clamps at 0:00 once expired. */
export function timerDisplay(
  timer: Pick<DiscussionTimer, 'startedAt' | 'expiresAt'>,
  clientNow: number,
  offsetMs: number,
): TimerDisplay {
  const remainingMs = Math.max(0, timer.expiresAt - (clientNow + offsetMs));
  const totalSec = Math.ceil(remainingMs / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  const expired = remainingMs <= 0;
  const urgent = !expired && totalSec <= URGENT_SEC;
  const total = Math.max(1, timer.expiresAt - timer.startedAt);
  const pct = Math.max(0, Math.min(1, remainingMs / total));
  return { remainingMs, min, sec, expired, urgent, pct };
}
